import { useEffect, useState, type ReactNode } from "react"
import { TableSummaryContext } from "./table.summary.context"
import { readSummaryState, writeSummaryState } from "./table.summary.state"
import type { SummaryAggregationType } from "./table.summary.types"

export type TableSummaryProviderProps = {
  /** Remembers the summary across visits when set; session-only otherwise. */
  tableId?: string
  children: ReactNode
}

/**
 * Owns the summary row's state for one table — whether it shows, and which
 * calculation each column runs — so a summary set up once is there the next
 * time the operator opens the table.
 */
function TableSummaryProvider(props: TableSummaryProviderProps) {
  const { tableId, children } = props
  const [state, setState] = useState(() => readSummaryState(tableId))

  useEffect(() => {
    if (tableId) writeSummaryState(tableId, state)
  }, [tableId, state])

  function setIsVisible(isVisible: boolean) {
    setState((prev) =>
      prev.isVisible === isVisible ? prev : { ...prev, isVisible },
    )
  }

  function setAggregation(
    columnId: string,
    type: SummaryAggregationType | null,
  ) {
    setState((prev) => {
      const aggregations = { ...prev.aggregations }
      if (type) aggregations[columnId] = type
      else delete aggregations[columnId]
      return { ...prev, aggregations }
    })
  }

  return (
    <TableSummaryContext.Provider
      value={{
        isVisible: state.isVisible,
        aggregations: state.aggregations,
        setIsVisible,
        setAggregation,
      }}
    >
      {children}
    </TableSummaryContext.Provider>
  )
}

export default TableSummaryProvider
